import { readData, storeData } from '../../utils/helpers/asyncStorageUnsecure';
import { USER_SETTING_KEYS, VIBRATION_TIME_IN_MS, DEFAULTS_USER_SETTING } from './constants';
import { SOUND_KEYS } from '../../components/GamePlayComponents/constant';

const CURRENT_CONFIG = {
  [USER_SETTING_KEYS.MUSIC]: null,
  [USER_SETTING_KEYS.SOUND]: null,
  [USER_SETTING_KEYS.VIBRATION]: null
};

// === SoundMode Definition (Replacement for NativeModules/Sounds) ===
const SoundMode = {
  allModes: [
    { mode: 'play' },
    { mode: 'playTone' },
    { mode: 'playQueue' },
    { mode: 'customMode' }
  ],
  currentMode: 'play'
};

// === VIBRATION REPLACEMENT (Web Alternative) ===
const vibrateMethod = (duration) => {
  if (navigator?.vibrate) {
    navigator.vibrate(duration);
  } else {
    // logger(TAG_NAMES.SOUND, 'Vibration API not supported by this browser', { isLogToFile: true });
  }
};

// === SOUND REPLACEMENT (Web Alternative) ===
const audioContext = new (window.AudioContext || window.webkitAudioContext)();
let activeOscillators = {};
let musicAudio = null;
let musicAudio2 = null;

const play = async (soundFile) => {
  try {
    // Stop any existing audio properly first
    if (musicAudio) {
      musicAudio.pause();
      musicAudio.currentTime = 0;
      musicAudio = null;
    }
    
    // Create and configure new audio instance
    musicAudio = new Audio(soundFile);
    // Wait for audio to be loaded before playing
    await musicAudio.load();
    await musicAudio.play();
  } catch (error) {
    console.warn('Audio playback failed:', error);
  }
};

const playRunningMusic = async (soundFile) => {
  try {
    // Stop any existing background music properly first
    if (musicAudio2) {
      musicAudio2.pause();
      musicAudio2.currentTime = 0;
      musicAudio2 = null;
    }

    // Create and configure new audio instance
    musicAudio2 = new Audio(soundFile);
    musicAudio2.loop = true;
    
    // iOS requires user interaction before playing audio
    // Set up audio to be ready to play
    await musicAudio2.load();
    
    // Set audio to play when ready
    musicAudio2.addEventListener('canplaythrough', async () => {
      try {
        // iOS requires play() to be called from a user interaction
        const playPromise = await musicAudio2.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.warn('Autoplay prevented:', error);
          });
        }
      } catch (error) {
        console.warn('Audio play failed:', error);
      }
    });
  } catch (error) {
    console.warn('Background music setup failed:', error);
  }
};

const stop = () => {
  if (musicAudio) {
    musicAudio.pause();
    musicAudio.currentTime = 0;
    musicAudio = null;
  }
};

export const stopRunningMusic = () => {
  if (musicAudio2) {
    musicAudio2.pause();
    musicAudio2.currentTime = 0;
    musicAudio2.src = '';
    musicAudio2.load();
    musicAudio2 = null;
  }
};

// Stop music when navigating away from the page
window.addEventListener('beforeunload', stopRunningMusic);
window.addEventListener('unload', stopRunningMusic);

const playTone = (frequency = 440) => {
  const oscillator = audioContext.createOscillator();
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime); // Hz
  oscillator.connect(audioContext.destination);
  oscillator.start();
  oscillator.stop(audioContext.currentTime + 1); // Play for 1 second
};

const playToneQueue = (frequencies = []) => {
  frequencies.forEach((freq, index) => {
    setTimeout(() => playTone(freq), index * 1000); // Plays one frequency at a time
  });
};

const changeTone = (soundKey, volume = 1, duration = 1) => {
  if (!activeOscillators[soundKey]) {
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(440, audioContext.currentTime); // Default frequency
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.start();
    activeOscillators[soundKey] = { oscillator, gainNode };
  }

  const { oscillator, gainNode } = activeOscillators[soundKey];
  gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
  oscillator.frequency.setValueAtTime(soundKey, audioContext.currentTime);
  setTimeout(() => release(soundKey), duration * 1000);
};

const release = (soundKey) => {
  if (activeOscillators[soundKey]) {
    activeOscillators[soundKey].oscillator.stop();
    activeOscillators[soundKey].oscillator.disconnect();
    activeOscillators[soundKey].gainNode.disconnect();
    delete activeOscillators[soundKey];
  }
};

// === REFACTORED FUNCTIONS (Main Business Logic) ===
export async function readUserSetting(key) {
  CURRENT_CONFIG[key] = await JSON.parse((await readData(key)) || DEFAULTS_USER_SETTING[key]);
  return CURRENT_CONFIG[key];
}

export async function toggelUserSetting(key) {
  CURRENT_CONFIG[key] = !(await readUserSetting(key));
  storeData(key, String(CURRENT_CONFIG[key]));

  if (key === USER_SETTING_KEYS.MUSIC) {
    playMusic();
  }

  return CURRENT_CONFIG[key];
}

export async function playMusic() {
  console.log('playMusic');
  if (CURRENT_CONFIG[USER_SETTING_KEYS.MUSIC] == null) {
    await readUserSetting(USER_SETTING_KEYS.MUSIC);
  }
  if (CURRENT_CONFIG[USER_SETTING_KEYS.MUSIC]) {
    // Add a small delay to ensure audio context is ready
    setTimeout(() => {
      // Resume audio context (required for iOS)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      playRunningMusic(SOUND_KEYS.MUSIC);
    }, 100);
  } else {
    stopRunningMusic(SOUND_KEYS.MUSIC);
  }
}

export function stopMusic() {
  // logger(TAG_NAMES.SOUND, 'Stop music', { isLogToFile: true });
  stop();
}

export async function playSound(soundKey) {
  if (CURRENT_CONFIG[USER_SETTING_KEYS.SOUND] == null) {
    await readUserSetting(USER_SETTING_KEYS.SOUND);
  }
  if (CURRENT_CONFIG[USER_SETTING_KEYS.SOUND]) {
    // logger(TAG_NAMES.SOUND, `Play ${soundKey} sound with current mode`, { isLogToFile: true });
    switch (SoundMode.currentMode) {
      case SoundMode.allModes[0].mode:
        play(soundKey);
        break;
      case SoundMode.allModes[1].mode:
        playTone(soundKey);
        break;
      case SoundMode.allModes[2].mode:
        playToneQueue([soundKey]);
        break;
      case SoundMode.allModes[3].mode:
        if (soundKey === 'tokenMove') {
          playTone(soundKey);
        } else {
          play(soundKey);
        }
        break;
      default:
        // logger(TAG_NAMES.SOUND, 'Unknown sound mode', { isLogToFile: true });
        break;
    }
  }
}

export async function vibrate() {
  if (CURRENT_CONFIG[USER_SETTING_KEYS.VIBRATION] == null) {
    await readUserSetting(USER_SETTING_KEYS.VIBRATION);
  }
  if (CURRENT_CONFIG[USER_SETTING_KEYS.VIBRATION]) {
    vibrateMethod(VIBRATION_TIME_IN_MS);
  }
}

export function changeSoundTone(soundKey, volume, duration) {
  changeTone(soundKey, volume, duration);
}

export function releaseSound(soundKey) {
  release(soundKey);
}
